'use client';

import {
  Image,
  ImageSize,
  Listing,
  Maybe,
  Resort,
  Scalars,
} from '@/utils/platform-api/__generated__/graphql';
import ReactImageGallery, {
  ReactImageGalleryItem,
  ReactImageGalleryProps,
} from 'react-image-gallery';

import './gallery.css';

import { Fragment, useEffect, useRef, useState } from 'react';
import {
  IMAGE_SIZES_BENTO_FULL,
  IMAGE_SIZES_BENTO_HALF,
  ImageSrcSetSize,
} from '@/utils/image';
import { Dialog, DialogPanel, TransitionChild } from '@headlessui/react';
import clsx from 'clsx';

import Button from '@/components/ui/button';
import FavoriteButton from '@/components/favorites/favorite-button';

import DriveImage, { getDriveImageURL } from './drive-image';
import { EmbeddedVideo } from './embedded-video';

import 'photoswipe/dist/photoswipe.css';

import { Gallery, Item } from 'react-photoswipe-gallery';

const media_gallery_main: ImageSrcSetSize[] = [
  { viewport: 600, image: 600 },
  { viewport: 1023, image: 1024 },
  { viewport: 4000, image: 800 },
];
const media_gallery_thumbnail: ImageSrcSetSize[] = [
  { viewport: 420, image: 420 },
];

/**
 * Determines if an item in the bento grid should span full width (2 columns).
 * Pattern: First image is always full width, then every 3rd image after pairs.
 * Layout: [full] [half, half] [full] [half, half] ...
 */
const isBentoFullWidth = (index: number): boolean => {
  if (index === 0) return true;
  return (index - 1) % 3 === 2;
};

export type MediaItem = {
  __typename?: 'Image' | 'Video';
  caption?: Maybe<Scalars['String']['output']>;
  category?: Maybe<Scalars['String']['output']>;
  external_id?: Maybe<Scalars['String']['output']>;
  size?: Maybe<ImageSize>;
  url?: Maybe<Scalars['String']['output']>;
};

export interface MediaGalleryProps {
  images: MediaItem[];
  listing?: Listing;
  resort?: Resort;
}

export const MediaGallery: React.FC<MediaGalleryProps> = (props) => {
  const ref = useRef<ReactImageGallery>(null);
  const [open, setOpen] = useState(false);
  const galleryProps: ReactImageGalleryProps = {
    lazyLoad: true,
    showNav: false,
    showIndex: true,
    showPlayButton: false,
    slideOnThumbnailOver: false,
    disableThumbnailScroll: true,
    useBrowserFullscreen: false,
    disableSwipe: false,
    showBullets: false,
    showFullscreenButton: false,
    showThumbnails: false,
    items: props.images.map((image, index) => ({
      mediaItem: image,
      original: image.url || '',
      lazyLoad: true,
      loading: 'lazy',
      renderItem: (item: ReactImageGalleryItem & { mediaItem: MediaItem }) => {
        return (
          <div className="relative flex h-full w-full items-center justify-center">
            {item.mediaItem.__typename === 'Image' && (
              <button
                type="button"
                className="relative flex h-full w-full items-center justify-center p-0"
                onClick={() => setOpen(true)}
              >
                <DriveImage
                  className="mx-auto aspect-video max-h-[400px] min-h-[215px] object-cover transition-opacity lg:h-[400px]"
                  image={item.mediaItem as Image}
                  srcsetSizes={media_gallery_main}
                  aspect={6 / 4}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                  alt={item.mediaItem.caption || `Image ${index + 1}`}
                />
                {typeof item.mediaItem?.caption === 'string' && (
                  <div
                    className={clsx(
                      'absolute inset-x-0 bottom-0 top-auto w-full whitespace-normal text-pretty py-2.5 pl-5 pr-[76px]',
                      'bg-transparent bg-gradient-to-t from-[rgba(0,0,0,0.8)] lg:pr-5'
                    )}
                  >
                    <p className="sm text-left leading-none text-white lg:text-center lg:drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]">
                      {item.mediaItem.caption}
                    </p>
                  </div>
                )}
              </button>
            )}
            {item.mediaItem.__typename === 'Video' && item.mediaItem.url && (
              <EmbeddedVideo
                src={item.mediaItem.url}
                className="mx-auto aspect-video max-h-[400px] min-h-[215px] w-full lg:h-[400px]"
                title={item.mediaItem.caption || undefined}
              />
            )}
          </div>
        );
      },
    })),
  };
  const galleryDesktopProps: ReactImageGalleryProps = {
    ...galleryProps,
    showIndex: false,
    disableSwipe: true,
    showThumbnails: true,
  };
  const [scrollToId, setScrollToId] = useState<string | undefined>(undefined);
  return (
    <div className="relative flex">
      {props.listing && (
        <FavoriteButton
          pos="left-[1rem] top-[1.75rem]"
          listingId={props.listing.id}
        />
      )}
      <div className="w-full lg:hidden">
        <ReactImageGallery ref={ref} {...galleryProps} />
      </div>
      <div className="hidden max-h-[400px] grid-cols-2 gap-[0.5rem] overflow-hidden rounded-xl lg:grid">
        <ReactImageGallery
          ref={ref}
          {...galleryDesktopProps}
          items={galleryDesktopProps.items.slice(0, 1)}
        />
        <div className="grid max-h-[200px] grid-cols-2 gap-[0.5rem]">
          {props.images.slice(1, 5).map((mediaItem, index) => {
            if (mediaItem.__typename === 'Video' && mediaItem.url) {
              return (
                <div
                  key={`-fullscreen-${index}`}
                  className="bg-black flex h-[200px] items-center justify-center overflow-hidden"
                >
                  <EmbeddedVideo
                    src={mediaItem.url}
                    className="h-full w-full"
                    title={mediaItem.caption || undefined}
                  />
                </div>
              );
            }

            return (
              <button
                key={`-fullscreen-${index}`}
                type="button"
                className={clsx(
                  'm-0 flex items-center justify-center bg-white p-0 transition-opacity hover:shadow-lg'
                )}
                onClick={() => {
                  setOpen(true);
                  setScrollToId(mediaItem.external_id || undefined);
                }}
              >
                {mediaItem.__typename === 'Image' && (
                  <DriveImage
                    className={clsx(
                      index >= 4
                        ? 'image-gallery-thumbnail-hidden'
                        : 'image-gallery-thumbnail-shown',
                      'h-[200px] object-cover'
                    )}
                    image={mediaItem as Image}
                    srcsetSizes={media_gallery_thumbnail}
                    loading="eager"
                    fetchPriority="high"
                    alt={mediaItem.caption || `Image ${index + 1}`}
                  />
                )}
              </button>
            );
          })}
          <ViewMoreImages
            open={open}
            setOpen={setOpen}
            images={props.images}
            scrollToId={scrollToId}
            setScrollToId={setScrollToId}
          />
        </div>
      </div>
    </div>
  );
};

const ViewMoreImages: React.FC<{
  images: MediaItem[];
  open: boolean;
  setOpen(a: boolean): void;
  setScrollToId(a: string | undefined): void;
  scrollToId?: string;
}> = ({ images, open, setOpen, setScrollToId, scrollToId }) => {
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setScrollToId(undefined);
  };
  return (
    <div className="hidden lg:block">
      {!open && (
        <Button
          onClick={handleOpen}
          style="outline"
          className="absolute bottom-[1rem] right-[1rem] z-[1] bg-white/80 hover:!border-primary-main hover:bg-white/90 hover:!text-primary-main"
          size="xsmall"
        >
          View More
        </Button>
      )}
      <Dialog open={open} onClose={handleClose} className="relative z-50">
        <div className="fixed inset-0 flex w-dvw items-center justify-center">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-100"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-100"
          >
            <DialogPanel
              transition
              className="max-lg:no-scrollbar relative h-dvh w-dvw overflow-y-scroll border bg-white transition data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <header className="sticky top-0 z-10 mb-2 flex h-fit items-center justify-start bg-white p-2 shadow-lg">
                <Button
                  onClick={handleClose}
                  style="outline"
                  className="!border-gray-5 !text-gray-5 hover:!border-gray-7 hover:!bg-transparent hover:!text-gray-7"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="20"
                    viewBox="0 0 12 20"
                    fill="none"
                  >
                    <path
                      d="M0.326417 9.20971C-0.108806 9.64494 -0.108806 10.3487 0.326417 10.7793L9.21608 19.6736C9.6513 20.1088 10.3551 20.1088 10.7857 19.6736C11.2163 19.2384 11.2209 18.5346 10.7857 18.104L2.6831 9.99682L10.7903 1.89426C11.2255 1.45904 11.2255 0.755276 10.7903 0.324683C10.3551 -0.10591 9.6513 -0.11054 9.22071 0.324683L0.326417 9.20971Z"
                      fill="currentColor"
                    />
                  </svg>
                </Button>
              </header>
              <div className="p-4">
                <DriveImageWithFullscreen
                  images={images}
                  className="mx-auto max-w-screen-lg"
                  aspect={6 / 4}
                  scrollToId={scrollToId}
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </div>
  );
};

const DriveImageWithFullscreen: React.FC<{
  images: MediaItem[];
  className?: string;
  aspect?: number;
  scrollToId?: string;
}> = ({ images, className, aspect = 6 / 4, scrollToId }) => {
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  const handleImageLoad = (index: number) => {
    setLoadedImages((prev) => new Set(prev).add(index));
  };

  useEffect(() => {
    if (scrollToId) {
      document
        .getElementById(scrollToId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [scrollToId]);

  return (
    <div className={className}>
      <Gallery
        options={{
          showHideAnimationType: 'fade',
          paddingFn: (viewport) => {
            const isMobile = viewport.x < 768;
            return {
              top: isMobile ? viewport.y * 0.1 : 75,
              bottom: isMobile ? viewport.y * 0.1 : 75,
              left: isMobile ? 0 : 75,
              right: isMobile ? 0 : 75,
            };
          },
          closeOnVerticalDrag: true,
          bgOpacity: 0.85,
          allowPanToNext: true,
          doubleTapAction: 'zoom',
          imageClickAction: 'zoom-or-close',
          bgClickAction: 'close',
          tapAction: 'close',
          pinchToClose: true,
          mainClass: '',
        }}
        withCaption
      >
        {/*
          Single image list for both breakpoints. Rendering mobile + desktop
          trees would register each photo twice in PhotoSwipe (e.g. 14 → 28).
        */}
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2">
          {images.map((image: MediaItem, index: number) => (
            <BentoGridItem
              key={`${image.external_id || 'item'}-${index}`}
              image={image}
              index={index}
              isFullWidth={isBentoFullWidth(index)}
              aspect={aspect}
              isLoaded={loadedImages.has(index)}
              onLoad={() => handleImageLoad(index)}
            />
          ))}
        </div>
      </Gallery>
    </div>
  );
};

const BentoGridItem: React.FC<{
  image: MediaItem;
  index: number;
  isFullWidth: boolean;
  aspect: number;
  isLoaded: boolean;
  onLoad: () => void;
}> = ({ image, index, isFullWidth, aspect, isLoaded, onLoad }) => {
  const srcsetSizes = isFullWidth
    ? IMAGE_SIZES_BENTO_FULL
    : IMAGE_SIZES_BENTO_HALF;

  if (image.__typename === 'Video' && image.url) {
    return (
      <div
        className={clsx(
          'group relative flex-none overflow-hidden shadow',
          isFullWidth && 'lg:col-span-2'
        )}
        id={image.external_id || undefined}
        style={{ aspectRatio: `${aspect}` }}
      >
        <EmbeddedVideo
          src={image.url}
          className="h-full w-full"
          title={image.caption || undefined}
        />
        {typeof image?.caption === 'string' && (
          <div className="absolute bottom-0 z-[1] mx-auto block max-h-[80%] w-full !bg-gradient-to-t !from-[rgba(0,0,0,0.9)] !via-40%">
            <p className="sm no-scrollbar p-[1rem] text-center text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]">
              {image.caption}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <Item
      original={getDriveImageURL(
        {
          url: image.url || '',
          width: image.size?.width || undefined,
          height: image.size?.height || undefined,
          alt: image.caption || undefined,
        },
        { width: 2400 }
      )}
      originalSrcset={[420, 640, 800, 1024, 1600, 2000, 2400, 3000]
        .map(
          (width) =>
            `${getDriveImageURL(
              {
                url: image.url || '',
                width: image.size?.width || undefined,
                height: image.size?.height || undefined,
                alt: image.caption || undefined,
              },
              { width: width }
            )} ${width}w`
        )
        .join(',')}
      width={image.size?.width || '100%'}
      height={image.size?.height || 'auto'}
      caption={`<div class="p-4 bg-[rgba(0,0,0,0.65)] text-base md:text-lg -mb-[15px] -mx-5">${image.caption || ''}</div>`}
    >
      {({ ref, open }) => (
        <div
          ref={ref}
          onClick={(e) => {
            open(e);
          }}
          className={clsx(
            'group relative flex-none cursor-pointer',
            isFullWidth && 'lg:col-span-2'
          )}
          id={image.external_id || undefined}
          style={{ aspectRatio: `${aspect}` }}
        >
          <DriveImage
            onLoad={onLoad}
            image={image as Image}
            loading={index === 0 ? 'eager' : 'lazy'}
            fetchPriority={index === 0 ? 'high' : 'auto'}
            aspect={aspect}
            srcsetSizes={srcsetSizes}
            className="h-full w-full object-cover object-center shadow transition-shadow group-hover:shadow-xl"
          />
          {isLoaded && typeof image?.caption === 'string' && (
            <div className="absolute bottom-0 z-[1] mx-auto block max-h-[80%] w-full !bg-gradient-to-t !from-[rgba(0,0,0,0.9)] !via-40%">
              <p className="sm no-scrollbar p-[1rem] text-center text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]">
                {image.caption}
              </p>
            </div>
          )}
        </div>
      )}
    </Item>
  );
};
